import BaseCarousel from '@PegasusCore/pegasus-one-carousel/src/script.js';

/**
 * Theme overrides for the Pegasus carousel. Kept here so vendor stays untouched.
 *
 * 1. Autoplay reset.
 *    The vendor autoplay is a plain setInterval that never restarts, and it
 *    doesn't keep the interval id, so navigating manually left the existing
 *    countdown running - click next a moment before it fired and autoplay
 *    advanced again almost immediately. Holding the id lets every slide change
 *    re-arm it, so a slide always gets the full interval on screen however it
 *    was reached. goToSlide is the single funnel for arrows, dots and drag-end.
 *
 * 2. Flicker on the infinite-loop wrap.
 *    goToSlide takes an `animate` flag, and scrollSlide honours it via the
 *    `no-transition` class - but fadeSlide ignores it and just sets opacities,
 *    so the inline `opacity 0.5s` each slide carries always runs. That made the
 *    supposedly-silent jump from the trailing clone back to the real slide
 *    crossfade two *identical* images, and with both at partial opacity
 *    mid-fade the stack composited to ~0.75, showing the background through as
 *    a dip. Suppressing the transition for that one jump makes it instant.
 */
class Carousel extends BaseCarousel {
    autoplayTimer = null;

    initAutoplay() {
        if (!this.autoplay || this.preview) return;

        this.restartAutoplay();
    }

    restartAutoplay() {
        clearInterval(this.autoplayTimer);

        this.autoplayTimer = setInterval(() => {
            if (this.isDragging) return;
            this.nextSlide();
        }, this.autoplay);
    }

    goToSlide(slide, animate = true, infiniteJump = false) {
        super.goToSlide(slide, animate, infiniteJump);

        // Only once autoplay is actually running, and never for the silent
        // repositioning an infinite loop does behind the scenes.
        if (this.autoplayTimer && !infiniteJump) {
            this.restartAutoplay();
        }
    }

    fadeSlide(slide, animate = true) {
        if (animate) {
            super.fadeSlide(slide, animate);
            return;
        }

        const transitions = this.slides.map((el) => el.style.transition);

        this.slides.forEach((el) => {
            el.style.transition = 'none';
        });

        super.fadeSlide(slide, animate);

        // Commit the new opacities while the transition is still off, otherwise
        // restoring it below just lets them animate from the old values anyway.
        void this.carousel.offsetWidth;

        this.slides.forEach((el, index) => {
            el.style.transition = transitions[index];
        });
    }
}

document.addEventListener('alpine:init', () => {
    Alpine.data(
        'carousel',
        (fade, overflow, autoplay, infinite, align, slidesInView, preview) =>
            new Carousel(fade, overflow, autoplay, infinite, align, slidesInView, preview),
    );
});
